import { FilterColors, FilterFonts } from "../../../state/filter";
import {
	PageElementsFamily,
	PageSizeAtom,
	createLocation,
} from "../../../state/page";
import { ReactElement, useCallback, useContext, useRef, useState } from "react";
import { clientConfig, strings } from "@quickpoint.me/shared";

import { ElementProps } from "../../../functionalComponents/RenderPage";
import FormButton from "./FormButton";
import { FormProps } from "../../../@types/viewerati";
import { IsInFlexBoxContext } from "../../displayElements/Cell";
import { NeuroDivergentFontMultiplier } from "../../../state/accessibility";
import { StackCfKey } from "../../../state/stack";
import { StackId } from "../../../state/selectors";
import { mainCssRules } from "../../../lib/cssTemplates";
import modifierStyled from "../../../lib/modifierStyled";
import { nanoid } from "nanoid";
import { opacityHelper } from "../../../lib/cssHelpers";
import styled from "styled-components";
import { useRecoilValue } from "recoil";
import { validate } from "email-validator";

const StyledForm = styled.div<FormProps & { $fontMultiplier: number; $baseFontSizeRaw: number }>`
  .invalid {
    color: red
  }
  ${(props) => opacityHelper(props.$tapped, props.theme.opacity)};
  ${(props) => `${modifierStyled("paragraph:", props)}`};
  /*  keep this here otherwise the text will become transparent */
  opacity: 1 !important;
  
  background-color: transparent !important;
  /* this required to prevent the backdrop from breaking out of the box */
  /* any modifiers will override it. */
  backdrop-filter: opacity(0);
  width: 100%;

  .email-message {
    margin-block: .5rem;
    font-size: ${(props) => props.$baseFontSize * 2}px;
  }

  .input-box-as-line {
    background: transparent !important;
    color: ${(props) => props.$filterColors.third} !important;
    outline-style: none !important;
    border-top: none !important;
    border-left: none !important;
    border-right: none !important;
    border-bottom: solid ${(props) => props.$filterColors.first} 1px;
    font-size: ${(props) => props.$baseFontSize * 3}px;
    box-shadow: 0 0 0 !important;
    -webkit-box-shadow: 0 0 0 !important;
    width: 100%;
    }
 
  .content {
    font-family: inherit;
    text-align: center;
    position: relative;
    margin: auto;
    width: 80%;
    display: flex;
    flex-direction: column;
    align-items: center;
    .sign-up-email-input {
      margin-block-start: .5rem;
    }  
  }

position: relative;
${(props) =>
	`${mainCssRules({
		baseFontSize: props.$baseFontSize,
		baseFontSizeRaw: props.$baseFontSizeRaw,
		fonts: props.$fonts,
		theme: props.theme,
		fontMultiplier: props.$fontMultiplier,
	})}`};
  ${(props) => `${modifierStyled("paragraph:", props)}`}
`;

const Form = ({
	atomId,
	pageIndex,
}: ElementProps): ReactElement<ElementProps> => {
	const [email, setEmail] = useState("");
	const [emailIsValid, setEmailIsValid] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const key = createLocation(pageIndex, atomId);
	const elementData = useRecoilValue(PageElementsFamily(key)).data;
	const isInFlexBox = useContext(IsInFlexBoxContext);
	const filterColors = useRecoilValue(FilterColors);
	const fontMultiplier = useRecoilValue(NeuroDivergentFontMultiplier);
	const fonts = useRecoilValue(FilterFonts);
	const pageSize = useRecoilValue(PageSizeAtom);
	const stackId = useRecoilValue(StackId);
	const stackCfKey = useRecoilValue(StackCfKey);
	const tapped = elementData.tapped;

	const baseFontSize = pageSize.baseFontSize;

	const elRef = useRef(null);

	const handleInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.currentTarget.value) {
			setEmailIsValid(validate(e.currentTarget.value));
			setEmail(e.currentTarget.value);
		}
	}, []);

	const handleClick = () => {
		////console.log("click");
		if (!emailIsValid || submitted) return;
		uploadRecord(email);
	};

	const uploadRecord = async (value: string) => {
		if (!emailIsValid) return;
		////console.log("uploading record");
		const record = {
			created: Date.now(),
			id: nanoid(26),
			stackId: stackId,
			recordType: "email",
			stack: stackCfKey,
			value,
		};

		// Lazy load GraphQL libraries only when form is submitted
		const [{ GraphQLClient }, { getSdk }] = await Promise.all([
			import("graphql-request"),
			import("@quickpoint.me/shared")
		]);
		const client = new GraphQLClient(clientConfig.gqlWriteUserRecord());
		const sdk = getSdk(client);

		await sdk.WriteUserRecord(record);
		setSubmitted(true);
	};

	return (
		<StyledForm
			$background={elementData.background}
			$baseFontSize={baseFontSize}
			$baseFontSizeRaw={pageSize.baseFontSizeRaw}
			$elementIndex={atomId}
			$isInFlexBox={isInFlexBox}
			$filterColors={filterColors}
			$fonts={fonts}
			$fontMultiplier={fontMultiplier}
			$pageSize={pageSize}
			$isInPortrait={!pageSize.landscape}
			$modifier={elementData.modifier}
			$tapped={tapped}
			$onePercentHeight={pageSize.onePercentHeight}
			$onePercentWidth={pageSize.onePercentWidth}
			className={`${strings.css.pageElement}  box`}
			data-atom-id={elementData.id}
			ref={elRef}
		>
			<div className="content">
				<span className="form-content">{elementData.content}</span>
				<input
					placeholder="Your E-mail"
					autoComplete="email"
					className={`sign-up-email-input input-box-as-line ${
						emailIsValid ? "" : "invalid"
					}`}
					id="sign-up-email"
					type="email"
					onInput={handleInput}
				/>
				{emailIsValid ? (
					<div className="email-message">👍</div>
				) : (
					<div className="email-message">Please enter a valid email</div>
				)}
				<div
					onClick={handleClick}
					onKeyDown={(e) => e.key === "Enter" && handleClick()}
					role="button"
					tabIndex={0}
				>
					<FormButton
						$disabled={!emailIsValid}
						atomId={atomId}
						submitted={submitted}
						pageIndex={pageIndex}
					/>
				</div>
			</div>
		</StyledForm>
	);
};

export default Form;
